import { useMutation, useQuery } from '@apollo/client/react';
import { CheckIcon, ChevronsUpDownIcon } from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { SearchBar } from '@/components/ui/search-bar';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { useOdooAuth } from '@/contexts/OdooAuthContext';
import {
  CREATE_COMMITTEE,
  type CreateCommitteeData,
  GET_ACTIVE_FUNDS,
  GET_COMMITTEES,
  type GetActiveFundsData
} from '@/lib/graphql/committees';
import { GET_ALL_USERS, type GetAllUsersData } from '@/lib/graphql/users';
import { cn } from '@/lib/utils';
import { getInitials } from '@/utils/getInitials';

interface CreateCommitteeFormState {
  committeeName: string;
  description: string;
  fundComboboxOpen: boolean;
  fundId: string | undefined;
  fundLinkage: 'linked' | 'none';
  threshold: string | undefined;
}

const CreateCommitteeStep1 = ({
  formState,
  funds,
  onNext,
  selectedFundName,
  setFormState
}: {
  formState: CreateCommitteeFormState;
  funds: { id: string; name: string }[];
  onNext: () => void;
  selectedFundName: null | string;
  setFormState: React.Dispatch<React.SetStateAction<CreateCommitteeFormState>>;
}) => {
  const isNextDisabled =
    formState.committeeName.trim().length === 0 ||
    !formState.threshold ||
    (formState.fundLinkage === 'linked' && !formState.fundId);

  return (
    <>
      <div className="grid max-h-full gap-4 overflow-y-auto">
        <div className="space-y-4">
          <div className="mb-1 text-lg font-semibold">Committee Details</div>
          <Separator />
          <div className="grid gap-4">
            <div className="flex w-full flex-row items-center gap-2.5">
              <div className="grid w-1/2 gap-2">
                <Field>
                  <FieldLabel htmlFor="committee-name">
                    Committee Name<span className="text-red-500">*</span>
                  </FieldLabel>
                  <Input
                    id="committee-name"
                    onChange={e => {
                      setFormState(prev => ({
                        ...prev,
                        committeeName: e.target.value
                      }));
                    }}
                    placeholder="Enter Value"
                    value={formState.committeeName}
                  />
                </Field>
              </div>
              <div className="grid w-1/2 gap-2">
                <Field>
                  <FieldLabel htmlFor="committee-threshold">
                    Threshold<span className="text-red-500">*</span>
                  </FieldLabel>
                  <Input
                    id="committee-threshold"
                    max={10}
                    min={1}
                    onChange={e => {
                      const value = e.target.value;
                      setFormState(prev => ({
                        ...prev,
                        threshold: value || undefined
                      }));
                    }}
                    placeholder="Enter Value"
                    type="number"
                    value={formState.threshold ?? ''}
                  />
                </Field>
              </div>
            </div>
            <div className="grid gap-2">
              <Field>
                <FieldLabel htmlFor="committee-description">
                  Description
                </FieldLabel>
                <Textarea
                  id="committee-description"
                  onChange={e => {
                    setFormState(prev => ({
                      ...prev,
                      description: e.target.value
                    }));
                  }}
                  placeholder="Enter description"
                  rows={4}
                  value={formState.description}
                />
              </Field>
            </div>
            <div className="grid gap-2">
              <Field>
                <FieldLabel>Fund Linkage</FieldLabel>
                <RadioGroup
                  className="flex w-full items-center gap-6"
                  onValueChange={value => {
                    setFormState(prev => ({
                      ...prev,
                      fundId: value === 'none' ? undefined : prev.fundId,
                      fundLinkage: value as 'linked' | 'none'
                    }));
                  }}
                  value={formState.fundLinkage}
                >
                  <div className="flex w-full items-center gap-2">
                    <div className="flex w-1/2 items-center gap-2">
                      <Field orientation="horizontal">
                        <RadioGroupItem
                          id="fund-linkage-none"
                          value="none"
                        />
                        <FieldLabel htmlFor="fund-linkage-none">
                          No Linkage
                        </FieldLabel>
                      </Field>
                    </div>
                    <div className="flex w-1/2 items-center gap-2">
                      <Field orientation="horizontal">
                        <RadioGroupItem
                          id="fund-linkage-linked"
                          value="linked"
                        />
                        <FieldLabel htmlFor="fund-linkage-linked">
                          Linked to Fund
                        </FieldLabel>
                      </Field>
                    </div>
                  </div>
                </RadioGroup>
              </Field>
            </div>
            {formState.fundLinkage === 'linked' && (
              <div className="grid gap-2">
                <Field>
                  <FieldLabel htmlFor="committee-fund">Select Fund</FieldLabel>
                  <Popover
                    modal={true}
                    onOpenChange={open => {
                      setFormState(prev => ({
                        ...prev,
                        fundComboboxOpen: open
                      }));
                    }}
                    open={formState.fundComboboxOpen}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        className="w-full justify-between"
                        id="committee-fund"
                        role="combobox"
                        type="button"
                        variant="outline"
                      >
                        {selectedFundName ?? 'Select an option'}
                        <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      align="start"
                      className="w-auto min-w-[var(--radix-popover-trigger-width)] p-0"
                    >
                      <Command>
                        <CommandInput placeholder="Search funds..." />
                        <CommandList>
                          <CommandEmpty>No funds found.</CommandEmpty>
                          <CommandGroup>
                            {funds.length > 0 ? (
                              funds.map(fund => (
                                <CommandItem
                                  key={fund.id}
                                  onSelect={() => {
                                    setFormState(prev => ({
                                      ...prev,
                                      fundComboboxOpen: false,
                                      fundId:
                                        prev.fundId === fund.id
                                          ? undefined
                                          : fund.id
                                    }));
                                  }}
                                  value={fund.name}
                                >
                                  <CheckIcon
                                    className={cn(
                                      'mr-2 h-4 w-4',
                                      formState.fundId === fund.id
                                        ? 'opacity-100'
                                        : 'opacity-0'
                                    )}
                                  />
                                  {fund.name}
                                </CommandItem>
                              ))
                            ) : (
                              <CommandItem
                                disabled
                                value="no-funds"
                              >
                                No funds available
                              </CommandItem>
                            )}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </Field>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="mt-auto flex flex-col-reverse p-4 sm:flex-row sm:justify-end sm:space-x-2">
        <Button
          className="bg-[#003455] hover:bg-[#003455]/90"
          disabled={isNextDisabled}
          onClick={onNext}
          type="button"
        >
          Next
        </Button>
      </div>
    </>
  );
};

const CreateCommitteeStep2 = ({
  isLoading,
  onCreate,
  selectedUserIds,
  threshold,
  toggleUser
}: {
  isLoading: boolean;
  onCreate: () => void;
  selectedUserIds: Set<string>;
  threshold: number;
  toggleUser: (userId: string) => void;
}) => {
  const [memberSearch, setMemberSearch] = useState('');
  const { role } = useOdooAuth();
  const isAdmin = role === 'admin';

  const { data: usersData } = useQuery<GetAllUsersData>(GET_ALL_USERS);

  const filteredUsers = useMemo(() => {
    const users = usersData?.users.edges.map(e => e.node) ?? [];
    if (!memberSearch) return users;
    return users.filter(u =>
      u.name.toLowerCase().includes(memberSearch.toLowerCase())
    );
  }, [usersData?.users.edges, memberSearch]);

  const meetsThreshold = selectedUserIds.size >= threshold;
  const isCreateDisabled = isLoading || !meetsThreshold;

  return (
    <>
      <div className="grid max-h-full gap-4 overflow-y-auto">
        <div className="space-y-4">
          <div className="mb-1 text-lg font-semibold">Add Members</div>
          {threshold > 0 && (
            <p className="text-muted-foreground text-sm">
              Select at least {threshold} member{threshold !== 1 ? 's' : ''}{' '}
              (selected: {selectedUserIds.size})
            </p>
          )}
          <SearchBar
            onChange={e => {
              setMemberSearch(e.target.value);
            }}
            placeholder="Search for members to add"
            value={memberSearch}
          />
          <div className="max-h-72 overflow-y-auto">
            {filteredUsers.map(user => (
              <label
                className="flex cursor-pointer items-center justify-between px-2 py-3 hover:bg-gray-50"
                key={user.id}
              >
                <div className="flex items-center gap-3">
                  <Avatar>
                    {user.partner?.image128 && (
                      <AvatarImage
                        alt={user.name}
                        src={
                          user.partner.image128.startsWith('data:')
                            ? user.partner.image128
                            : `data:image/png;base64,${user.partner.image128}`
                        }
                      />
                    )}
                    <AvatarFallback>{getInitials(user.name)}</AvatarFallback>
                  </Avatar>
                  <span className="text-xs">{user.name}</span>
                </div>
                <Checkbox
                  checked={selectedUserIds.has(user.id)}
                  onCheckedChange={() => {
                    toggleUser(user.id);
                  }}
                />
              </label>
            ))}
          </div>
        </div>
      </div>
      {isAdmin && (
        <div className="mt-auto flex flex-row justify-end p-4">
          {isCreateDisabled ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <Button
                    className="bg-[#003455] hover:bg-[#003455]/90"
                    disabled
                    onClick={onCreate}
                    type="button"
                  >
                    {isLoading ? 'Creating...' : 'Create Committee'}
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {!meetsThreshold && threshold > 0
                  ? `Select at least ${String(threshold)} member${threshold !== 1 ? 's' : ''} to create the committee.`
                  : 'Creating committee...'}
              </TooltipContent>
            </Tooltip>
          ) : (
            <Button
              className="bg-[#003455] hover:bg-[#003455]/90"
              onClick={onCreate}
              type="button"
            >
              Create Committee
            </Button>
          )}
        </div>
      )}
    </>
  );
};

export const CreateCommitteeForm = ({
  onClose,
  onSuccess
}: {
  onClose: () => void;
  onSuccess?: (committeeId: string) => void;
}) => {
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const [formState, setFormState] = useState<CreateCommitteeFormState>({
    committeeName: '',
    description: '',
    fundComboboxOpen: false,
    fundId: undefined,
    fundLinkage: 'linked',
    threshold: undefined
  });
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(
    () => new Set()
  );

  const { data: fundsData } = useQuery<GetActiveFundsData>(GET_ACTIVE_FUNDS);
  const [createCommittee, { loading: createLoading }] =
    useMutation<CreateCommitteeData>(CREATE_COMMITTEE, {
      refetchQueries: [{ query: GET_COMMITTEES }]
    });
  const funds = useMemo(
    () => fundsData?.funds.edges.map(edge => edge.node) ?? [],
    [fundsData?.funds.edges]
  );

  const selectedFundName = useMemo(() => {
    if (!formState.fundId) return null;
    const fund = funds.find(f => f.id === formState.fundId);
    return fund?.name ?? null;
  }, [formState.fundId, funds]);

  const resetForm = useCallback(() => {
    setCurrentStep(1);
    setFormState({
      committeeName: '',
      description: '',
      fundComboboxOpen: false,
      fundId: undefined,
      fundLinkage: 'linked',
      threshold: undefined
    });
    setSelectedUserIds(new Set());
  }, []);

  const toggleUser = useCallback((userId: string) => {
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }, []);

  const handleNext = useCallback(() => {
    setCurrentStep(2);
  }, []);

  const handleBack = useCallback(() => {
    setCurrentStep(1);
  }, []);

  const handleCreate = useCallback(() => {
    const members = Array.from(selectedUserIds).map(userId => ({
      role: 'member',
      userId
    }));

    createCommittee({
      variables: {
        input: {
          description: formState.description.trim() || undefined,
          fundId:
            formState.fundLinkage === 'linked' ? formState.fundId : undefined,
          members: members.length > 0 ? members : undefined,
          name: formState.committeeName,
          threshold: formState.threshold ? parseInt(formState.threshold, 10) : 1
        }
      }
    })
      .then(response => {
        const committeeId = response.data?.createCommittee.committee?.id;
        resetForm();
        if (committeeId && onSuccess) {
          onSuccess(committeeId);
        }
        onClose();
      })
      .catch((error: unknown) => {
        console.error('Failed to create committee:', error);
      });
  }, [
    createCommittee,
    formState.committeeName,
    formState.description,
    formState.threshold,
    formState.fundLinkage,
    formState.fundId,
    selectedUserIds,
    resetForm,
    onClose,
    onSuccess
  ]);

  return (
    <div className="flex h-full flex-col overflow-hidden px-6 py-4">
      <div className="mb-4 flex flex-row items-center gap-2">
        <h2 className="text-xl font-semibold">Create Committee</h2>
        <Badge variant="yellow">Unsaved</Badge>
      </div>
      <Breadcrumb className="mb-6">
        <BreadcrumbList>
          <BreadcrumbItem>
            {currentStep === 1 ? (
              <BreadcrumbPage>Step 1</BreadcrumbPage>
            ) : (
              <BreadcrumbLink
                className="cursor-pointer"
                onClick={handleBack}
              >
                Step 1
              </BreadcrumbLink>
            )}
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            {currentStep === 2 ? (
              <BreadcrumbPage>Step 2</BreadcrumbPage>
            ) : (
              'Step 2'
            )}
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
      <div className="flex-1 overflow-hidden">
        {currentStep === 1 ? (
          <CreateCommitteeStep1
            formState={formState}
            funds={funds}
            onNext={handleNext}
            selectedFundName={selectedFundName}
            setFormState={setFormState}
          />
        ) : (
          <CreateCommitteeStep2
            isLoading={createLoading}
            onCreate={handleCreate}
            selectedUserIds={selectedUserIds}
            threshold={
              formState.threshold != null && formState.threshold !== ''
                ? parseInt(formState.threshold, 10) || 0
                : 0
            }
            toggleUser={toggleUser}
          />
        )}
      </div>
    </div>
  );
};
